import axios from "axios";
import { AxiosResponse } from "axios";
import JSZip from "jszip";
import React from "react";

import { useDeleteBuildMutation, useFetchProductBuildsQuery, useImportProductBuildMutation } from "@dashboard/products/api";
import { calculateDownloadProgress, calculateDownloadSpeed } from "@dashboard/products/components/shared";
import { Build } from "@dashboard/products/types";
import { Product } from "@dashboard/products/types";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { showWarningToast } from "@shared/toasts/Toasts";
import { showErrorToast } from "@shared/toasts/Toasts";

import { Props } from "./MainButton";

type ZipContent = {
    product: Product;
    build: Build;
    files: { name: string; content: Blob }[];
};
const validateZipContent = async (file: File): Promise<ZipContent> => {
    const arrayBuffer = await file.arrayBuffer();
    const zip = new JSZip();
    const zipFile = await zip.loadAsync(arrayBuffer);

    const metadata = zipFile.file("metadata.json");
    if (!metadata) {
        throw new Error("Metadata file not found in ZIP");
    }

    const jsonMetadataText = await metadata.async("string");
    const jsonMetadata: { build: Build; product: Product } = JSON.parse(jsonMetadataText);
    if (!jsonMetadata.build) {
        throw new Error("Build file not found in ZIP");
    }
    if (!jsonMetadata.product) {
        throw new Error("Product file not found in ZIP");
    }

    const files = zipFile.files;
    const filesArray = Object.values(files);
    const filesArrayWithContent = await Promise.all(
        filesArray
            .filter((item) => item.name !== "metadata.json")
            .map(async (item) => {
                const content = await item.async("blob");
                return { name: item.name, content };
            }),
    );

    return { ...jsonMetadata, files: filesArrayWithContent };
};

export const getBuildButtonTooltip = (props: Props, mayBuild: boolean) => {
    if (props.cannotManageProducts) {
        return {
            title: "Your role does not allow you to manage the product",
            disabled: true,
        };
    }
    if (props.isBuildLimitReached) {
        return {
            title: "Build limit has been reached",
            disabled: true,
        };
    }
    if (!mayBuild) {
        return {
            title: "Building is disabled by configuration. Adjust settings to continue",
            disabled: true,
        };
    }

    return {
        title: "",
        disabled: false,
    };
};
export const getImportButtonTooltip = (props: Props, mayImport: boolean) => {
    if (props.cannotManageProducts) {
        return {
            title: "Your role does not allow you to manage the product",
            disabled: true,
        };
    }
    if (props.isBuildLimitReached) {
        return {
            title: "Build limit has been reached",
            disabled: true,
        };
    }
    if (!mayImport) {
        return {
            title: "Import is disabled by configuration. Adjust settings to continue",
            disabled: true,
        };
    }

    return {
        title: "",
        disabled: false,
    };
};
export const getSplitButtonTooltip = (props: Props, mayBuild: boolean, mayImport: boolean) => {
    if (props.cannotManageProducts) {
        return {
            title: "Your role does not allow you to manage the product",
            disabled: true,
        };
    }
    if (props.isBuildLimitReached) {
        return {
            title: "Build limit has been reached",
            disabled: true,
        };
    }
    if (!mayBuild && !mayImport) {
        return {
            title: "Building and importing are disabled by configuration. Adjust settings to continue",
            disabled: true,
        };
    }

    return {
        title: "",
        disabled: false,
    };
};

export const useImportFile = (product: Product) => {
    const [buildId, setBuildId] = React.useState<number | null>(null);
    const [name, setName] = React.useState<string | null>(null);
    const [speed, setSpeed] = React.useState("0 MB/s");
    const [progress, setProgress] = React.useState(0);
    const [isUploading, setIsUploading] = React.useState(false);
    const [currentFileIndex, setCurrentFileIndex] = React.useState(0);
    const [totalFiles, setTotalFiles] = React.useState(0);

    const abortControllerRef = React.useRef<AbortController | null>(null);
    const ref = React.useRef<HTMLInputElement>(null);

    const { data: builds = [] } = useFetchProductBuildsQuery(product.id);
    const [importProductBuild] = useImportProductBuildMutation();
    const [deleteBuild] = useDeleteBuildMutation();

    const onClear = () => {
        setIsUploading(false);
        setName(null);
        setSpeed("0 MB/s");
        setProgress(0);
        setBuildId(null);
        setCurrentFileIndex(0);
        setTotalFiles(0);
        abortControllerRef.current = null;
        if (ref.current) {
            ref.current.value = "";
        }
    };
    const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsUploading(true);
        abortControllerRef.current = new AbortController();
        const file = e.target.files?.[0];
        if (file) {
            try {
                const zipContent = await validateZipContent(file);

                const currentProductVersion = product.androidVersion.split(".")[0];
                const zipProductVersion = zipContent.product.androidVersion.split(".")[0];
                if (currentProductVersion !== zipProductVersion) {
                    throw new Error(
                        `Product Android version (${currentProductVersion}) does not match zip file version (${zipProductVersion})`,
                    );
                }

                if (product.codename !== zipContent.product.codename) {
                    throw new Error(
                        `Product codename (${product.codename}) does not match zip file codename (${zipContent.product.codename})`,
                    );
                }

                if (builds.some((build) => build.version === zipContent.build.version)) {
                    throw new Error(`Build version ${zipContent.build.version} already exists`);
                }

                const importResult = await importProductBuild({
                    productId: product.id,
                    ...zipContent,
                });
                if ("error" in importResult) {
                    return;
                }
                const result = importResult.data;

                setBuildId(result.build.buildId);

                if (result.uploadURLs.length) {
                    setTotalFiles(result.uploadURLs.length);
                    let fileIndex = 0;
                    for (const uploadURL of result.uploadURLs) {
                        const fileMeta = result.build.artifacts?.find((bf) => bf.id === uploadURL.id);

                        if (!fileMeta) {
                            continue;
                        }
                        const matchingFile = zipContent.files.find((item) => {
                            return item.name === fileMeta.name;
                        });
                        if (!matchingFile) {
                            continue;
                        }

                        fileIndex += 1;
                        setCurrentFileIndex(fileIndex);
                        setName(fileMeta.name);
                        const uploadStartTime = Date.now();
                        const presignedApi = axios.create({ url: uploadURL.uploadURL });
                        // eslint-disable-next-line no-await-in-loop
                        await presignedApi.put<Blob, AxiosResponse<Blob>>(uploadURL.uploadURL, matchingFile.content, {
                            headers: { "Content-Type": matchingFile.content.type },
                            signal: abortControllerRef.current?.signal,
                            onUploadProgress: (progressEvent) => {
                                if (progressEvent.total) {
                                    const percent = calculateDownloadProgress(progressEvent.loaded, progressEvent.total);
                                    setProgress(percent);

                                    const downloadSpeed = calculateDownloadSpeed(progressEvent.loaded, uploadStartTime);
                                    setSpeed(downloadSpeed);
                                }
                            },
                        });
                    }
                }

                showSuccessToast("Imported build successfully");
            } catch (err) {
                const error = err as Error;
                if (error.name === "CanceledError") {
                    showWarningToast("Upload cancelled");
                } else {
                    showErrorToast(error.message);
                }
            } finally {
                onClear();
            }
        }
    };
    const onAbort = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (product.id && buildId) {
            await deleteBuild({ productId: product.id, buildId });
        }
        onClear();
    };

    return { ref, isUploading, name, speed, progress, currentFileIndex, totalFiles, onAbort, onChange };
};
